import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from '../components/Toast';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
}

type ToastAction =
  | { type: 'ADD'; toast: Toast }
  | { type: 'REMOVE'; id: string };

function toastReducer(
  state: { toasts: Toast[] },
  action: ToastAction
): { toasts: Toast[] } {
  switch (action.type) {
    case 'ADD':
      // Cap queue at 5
      return { toasts: [...state.toasts, action.toast].slice(-5) };
    case 'REMOVE':
      return { toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Module-level emit function — allows api.ts (outside the React tree) to show toasts. */
let _emitToast: ((toast: Omit<Toast, 'id'>) => void) | null = null;

/**
 * Emit a toast from outside the React component tree (e.g. Axios interceptors).
 * Falls back silently if the ToastProvider has not mounted yet.
 */
export function emitToast(toast: Omit<Toast, 'id'>): void {
  _emitToast?.(toast);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    dispatch({ type: 'ADD', toast: { ...toast, id } });
    setTimeout(() => dispatch({ type: 'REMOVE', id }), toast.duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE', id });
  }, []);

  // Register / deregister the module-level emitter
  useEffect(() => {
    _emitToast = addToast;
    return () => {
      _emitToast = null;
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {createPortal(
        <ToastContainer toasts={state.toasts} onRemove={removeToast} />,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToastContext(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastContext must be used within <ToastProvider>');
  return ctx;
}
